// shared-components/context/AuthContext.tsx

import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { authService } from "@shared/services";
import type { User } from "@supabase/supabase-js";
import type { MFAFactor } from "@shared/services/AuthService";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  lastError: string | null;
  retryAuth: () => Promise<void>;
  isSigningOut: boolean;
  getAuthToken: () => Promise<string | null>;
  mfaFactors: MFAFactor[];
  isMFAEnabled: boolean;
  refreshMFAStatus: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([]);
  const [isMFAEnabled, setIsMFAEnabled] = useState(false);

  // Retry authentication mechanism
  const retryAuth = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLastError(null);

    try {
      const session = await authService.getCurrentSession();
      setUser(session?.user || null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Authentication retry failed";
      setLastError(errorMessage);
      console.error("Auth retry failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check MFA status for authenticated user
  const checkMFAStatus = useCallback(async (): Promise<void> => {
    if (!user) {
      setMfaFactors([]);
      setIsMFAEnabled(false);
      return;
    }

    try {
      const factors = await authService.listMFAFactors();
      setMfaFactors(factors);
      setIsMFAEnabled(factors.some((f) => f.status === "verified"));
    } catch (error) {
      console.error("Failed to check MFA status:", error);
      setMfaFactors([]);
      setIsMFAEnabled(false);
    }
  }, [user]);

  useEffect(() => {
    // Check for existing session with retry mechanism
    const checkSession = async () => {
      try {
        setLastError(null);
        const session = await authService.getCurrentSession();
        setUser(session?.user || null);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Session check failed";
        setLastError(errorMessage);
        console.error("Session check failed:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Subscribe to auth changes with error handling
    const {
      data: { subscription },
    } = authService.onAuthStateChange((_event, session) => {
      try {
        setUser(session?.user || null);
        setLastError(null); // Clear errors on successful auth change
      } catch (error) {
        console.error("Auth state change error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Auth state change failed";
        setLastError(errorMessage);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Enhanced sign out with retry mechanism and fallback
  const signOut = useCallback(async (): Promise<void> => {
    if (isSigningOut) {
      return; // Prevent multiple concurrent sign out attempts
    }

    setIsSigningOut(true);
    setLastError(null);

    const maxRetries = 3;
    let _lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await authService.logout();
        setUser(null);
        setLastError(null);
        setIsSigningOut(false); // Reset signing out state on success
        return; // Success - exit function
      } catch (error) {
        _lastError =
          error instanceof Error ? error : new Error("Sign out failed");
        console.error(`Sign out attempt ${attempt} failed:`, error);

        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed - implement fallback behavior
    console.error("All sign out attempts failed, implementing fallback");

    // Graceful degradation: clear user state locally even if server sign out failed
    setUser(null);
    setLastError(
      `Sign out failed after ${maxRetries} attempts. You have been signed out locally, but you may need to clear your browser cache or sign out manually if you experience issues.`,
    );

    // Optional: Clear local storage as additional fallback
    try {
      localStorage.removeItem("supabase.auth.token");
      sessionStorage.clear();
    } catch (storageError) {
      console.error("Failed to clear local storage:", storageError);
    }

    setIsSigningOut(false); // Reset signing out state after fallback
    // Don't throw error - we've handled it gracefully
  }, [isSigningOut]);

  // Get authentication token for API calls
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      return await authService.getAccessToken();
    } catch (error) {
      console.error("Failed to get auth token:", error);
      return null;
    }
  }, []);

  // Cleanup effect for sign out state
  useEffect(() => {
    if (isSigningOut) {
      const timeout = setTimeout(() => {
        setIsSigningOut(false);
      }, 30000); // Reset after 30 seconds as fallback

      return () => clearTimeout(timeout);
    }
  }, [isSigningOut]);

  // Check MFA status when user changes
  useEffect(() => {
    if (user) {
      checkMFAStatus();
    }
  }, [user, checkMFAStatus]);

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated: !!user,
    signOut,
    lastError,
    retryAuth,
    isSigningOut,
    getAuthToken,
    mfaFactors,
    isMFAEnabled,
    refreshMFAStatus: checkMFAStatus,
  };

  if (loading) {
    return <div>Loading...</div>; // You might want to replace this with a proper loading component
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
